import type { DealDetailResource, TeamResource, UpdateDealBody } from '@majuscule/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { internalFetch } from '../../lib/api';
import { crmSend, errorMessage } from '../../lib/crm';
import { useCan } from '../../lib/me';
import { useCrmRefresh } from '../crm/shared';
import { NativeSelect } from '@/components/ui/native-select';

/** Active members, for the owner pickers (admins only: the team list is admin-only). */
export function useTeamMembers(enabled: boolean) {
  return useQuery({
    queryKey: ['team'],
    queryFn: () => internalFetch<TeamResource>('/internal/team'),
    enabled,
    select: (team) => team.members.filter((m) => m.disabledAt === null),
  });
}

/**
 * Porteur d'une affaire (lot 20) : un administrateur peut le changer, un
 * commercial le voit seulement (c'est lui).
 */
export function DealOwner({ detail }: { detail: DealDetailResource }): JSX.Element {
  const canAssign = useCan('all_deals');
  const refresh = useCrmRefresh();
  const members = useTeamMembers(canAssign);
  const assign = useMutation({
    mutationFn: (ownerId: string) => {
      const body: UpdateDealBody = { ownerId };
      return crmSend<DealDetailResource>('PATCH', `/deals/${detail.deal.id}`, body);
    },
    onSuccess: () => {
      refresh();
    },
  });
  const owner = detail.deal.owner;
  if (!canAssign || members.data === undefined) {
    return <span>{owner?.name ?? '—'}</span>;
  }
  return (
    <span className="grid gap-1">
      <NativeSelect
        aria-label="Porteur de l’affaire"
        controlSize="sm"
        value={owner?.id ?? ''}
        disabled={assign.isPending}
        onChange={(event) => {
          if (event.target.value !== '') {
            assign.mutate(event.target.value);
          }
        }}
      >
        {owner === null ? <option value="">Aucun porteur</option> : null}
        {members.data.map((member) => (
          <option key={member.id} value={member.id}>
            {member.displayName ?? member.email}
          </option>
        ))}
      </NativeSelect>
      {assign.isError ? (
        <span className="text-caption text-destructive">
          {errorMessage(assign.error, 'Changement de porteur refusé.')}
        </span>
      ) : null}
    </span>
  );
}
